import { inject, injectable } from "inversify";
import get from "lodash/get";
import type { WhatsappSelectorsType } from "@whatslly/types/integrations/im/WhatsappGetSelectorsResponse";
import { retry } from "@whatslly/commons/utils";
import { MessageScraper } from "./messageScraper";
import { contentBackend } from "@whatslly/commons/contentBackend";
import { ContentCommands } from "@whatslly/commons/contentCommands";
import { BaseBlockMessagingService } from "../blockMessaging";
import { FrontLogger } from "@whatslly/commons/frontLogger/frontLogger";
import type { IStorage } from "@whatslly/commons/storage.constants";
import { InjectStorage } from "@whatslly/commons/storage.constants";
import type { ListenerService } from "@whatslly/types/instant-messaging/ListenerService";
import { IListenerServiceSymbol } from "@whatslly/types/instant-messaging/ListenerService";
import { IMUserStore } from "../IMUserStore";
import type { ISendMessage } from "../sendMessage";
import { InjectSendMessage } from "../sendMessage";
import type { InstantMessagingService } from "@whatslly/types/instant-messaging/InstantMessagingService";
import { IInstantMessagingServiceSymbol } from "@whatslly/types/instant-messaging/InstantMessagingService";
import type { IImTemplatesStore } from "@whatslly/instant-messaging/commons/IImTemplatesStore";
import { ImTemplatesStoreSymbol } from "@whatslly/instant-messaging/commons/IImTemplatesStore";
import { ContactService } from "@whatslly/instant-messaging/contactService";
import { OutboundMessageType } from "@whatslly/types/policy/enums";
import { type IMessageEmitter, InjectMessageEmitter } from "@whatslly/types/instant-messaging/messagesEmitter";
import { IncidentsChannel } from "@whatslly/types/incident/IncidentsChannels";

declare const window: {
  importNamespace: (module: string) => any;
  importDefault: (module: string) => any;
  Debug?: {
    VERSION?: string;
  };
};

@injectable()
export class BlockMessagingService extends BaseBlockMessagingService {
  private _textInputElement: HTMLElement | undefined = undefined;
  override channelType: IncidentsChannel = IncidentsChannel.WHATSAPP_WEB;
  #selectorsToHide = new Map<string, { elements: HTMLElement[]; predicate: () => boolean | undefined }>();
  #abortController = new AbortController();
  #isLockedToChannelsTab = false;
  #chatListVisibilityChangeHandler: ((isVisible: boolean) => void) | undefined;
  private enqueueEventJob(
    event: {
      eventName: string | undefined;
      eventDescription: string | undefined;
      eventLocation: string | undefined;
    },
    executeOriginal: () => void | Promise<void>
  ) {
    this.jobQueue.enqueue({
      kind: "event",
      message: `${event.eventName ?? ""}\n${event.eventDescription ?? ""}\n${event.eventLocation ?? ""}`.trim(),
      phone: this.instantMessagingService.getSelectedContact?.()?.phone,
      shouldScan: this.shouldScanCurrentContact(),
      executeOriginal: async () => await executeOriginal(),
    });
  }
  private enqueueOrderJob(message: string, executeOriginal: () => void | Promise<void>) {
    this.jobQueue.enqueue({
      kind: "order",
      message,
      phone: this.instantMessagingService.getSelectedContact?.()?.phone,
      shouldScan: this.shouldScanCurrentContact(),
      executeOriginal: async () => await executeOriginal(),
    });
  }
  private enqueuePollJob(poll: { pollName: string | undefined; pollOptions: string[] }, executeOriginal: () => void | Promise<void>) {
    const optionsText = poll.pollOptions?.join("\n");
    this.jobQueue.enqueue({
      kind: "poll",
      message: `${poll.pollName ? `${poll.pollName}\n` : ""}${optionsText ?? ""}`.trim(),
      phone: this.instantMessagingService.getSelectedContact?.()?.phone,
      shouldScan: this.shouldScanCurrentContact(),
      executeOriginal: async () => await executeOriginal(),
    });
  }
  private enqueueContactJob(body: string, executeOriginal: () => void | Promise<void>) {
    this.jobQueue.enqueue({
      kind: "contact",
      message: body,
      phone: this.instantMessagingService.getSelectedContact?.()?.phone,
      shouldScan: this.shouldScanCurrentContact(),
      executeOriginal: async () => await executeOriginal(),
    });
  }
  private enqueueTextJob(message: string, executeOriginal: () => void | Promise<void>) {
    this.jobQueue.enqueue({
      kind: "text",
      message,
      phone: this.instantMessagingService.getSelectedContact?.()?.phone,
      shouldScan: this.shouldScanCurrentContact(),
      executeOriginal: async () => await executeOriginal(),
    });
  }
  private enqueueAttachmentJob(file: File, caption: string, mimeType: string, executeOriginal: () => void | Promise<void>) {
    this.jobQueue.enqueue({
      kind: "attachment",
      file,
      caption,
      mimeType,
      phone: this.instantMessagingService.getSelectedContact?.()?.phone,
      shouldScan: this.shouldScanCurrentContact(),
      executeOriginal: async () => await executeOriginal(),
    });
  }
  constructor(
    @inject(FrontLogger) frontLogger: FrontLogger,
    @inject(InjectStorage) storage: IStorage,
    @inject(ImTemplatesStoreSymbol) templatesStore: IImTemplatesStore,
    @inject(IListenerServiceSymbol) domListenerService: ListenerService,
    @inject(IMUserStore) imUserStore: IMUserStore,
    @inject(InjectSendMessage) sendMessage: ISendMessage,
    @inject(IInstantMessagingServiceSymbol) instantMessagingService: InstantMessagingService,
    @inject(ContactService) contactService: ContactService,
    @inject(InjectMessageEmitter) messageEmitter: IMessageEmitter<any, any>
  ) {
    super(
      frontLogger,
      storage,
      templatesStore,
      domListenerService,
      imUserStore,
      sendMessage,
      instantMessagingService,
      contactService,
      messageEmitter
    );
  }

  override reset() {
    super.reset();
    this.#abortController.abort();
    this.#abortController = new AbortController();
    [...this.#selectorsToHide.keys()].forEach((selector) => this.showElements(selector));
    this.#selectorsToHide.clear();
    this._textInputElement = undefined;
    this.#isLockedToChannelsTab = false;
    this.#cleanupChannelsLock();
  }

  addPasteEventListener() {
    if (this._textInputElement) {
      this._textInputElement.removeEventListener("paste", this.shouldBlockPasteEvent);
    }
    this._textInputElement =
      document.querySelector<HTMLElement>(this.selectors?.attachFile.pasteElementCssSelector ?? "[data-testid='conversation-compose-box-input']") ||
      undefined;
    if (!this._textInputElement) {
      return;
    }
    this._textInputElement.addEventListener("paste", this.shouldBlockPasteEvent, true);
  }

  private async waitForElements(selector: string, signal: AbortSignal) {
    try {
      return await retry(
        async () => {
          const elements = document.querySelectorAll<HTMLElement>(selector);
          if (elements.length === 0) {
            throw new Error("failed to find element");
          }

          return Array.from(elements);
        },
        { maxRetry: Number.MAX_SAFE_INTEGER, delayBetweenRetryMs: 200, signal }
      );
    } catch {
      this.logger.error("failed to elements");
      return [];
    }
  }

  private async hideElements(selector: string, predicate: () => boolean | undefined) {
    this.#selectorsToHide.delete(selector);
    if (!predicate()) {
      return this.showElements(selector);
    }
    const signal = this.#abortController.signal;
    const elements = await this.waitForElements(selector, signal);
    if (signal.aborted) {
      return;
    }
    this.#selectorsToHide.set(selector, { elements, predicate });
    elements.forEach((element) => {
      if (element.style) {
        element.style.display = "none";
      }
    });
  }

  private async showElements(selector: string) {
    document.querySelectorAll<HTMLElement>(selector).forEach((element) => {
      if (element.style) {
        element.style.display = "";
      }
    });
  }

  private async hideForbiddenElements() {
    this.#abortController.abort();
    this.#abortController = new AbortController();
    if (!this.selectors) {
      return;
    }
    this.hideElements(this.selectors?.attachFile.recordButton, () => this.shouldBlockUploadSync({ mimeType: "audio/ogg" }));
    this.hideElements(this.selectors?.attachFile.attachmentDropdownDocuments, () => this.shouldBlockUploadSync({}));
    this.hideElements(
      this.selectors?.attachFile.attachmentDropdownPhotosVideos,
      () => this.shouldBlockUploadSync({ mimeType: "video/*" }) || this.shouldBlockUploadSync({ mimeType: "image/*" })
    );
    this.hideElements(this.selectors?.attachFile.attachmentDropdownCamera, () => this.shouldBlockUploadSync({ mimeType: "image/*" }));
    this.hideElements(this.selectors?.attachFile.attachmentDropdownSticker, () => this.shouldBlockUploadSync({ mimeType: "image/*" }));
  }

  private observeElements() {
    const observer = new MutationObserver(() => {
      this.#selectorsToHide.forEach(({ elements, predicate }, selector) => {
        let shouldHide = false;
        for (const element of elements) {
          if (!element.isConnected) {
            shouldHide = true;
          } else if (!element.matches(selector)) {
            element.style.display = "";
            shouldHide = true;
          }
        }
        if (shouldHide) {
          this.hideElements(selector, predicate);
        }
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  private monkeyPatchWhatsappComet() {
    if (!MessageScraper.whatsappAPI || !this.selectors) {
      return;
    }
    const selectors = this.selectors;
    const sendMsg = window.importNamespace("WAWebSendMsgChatAction");
    const editEvent = window.importNamespace("WAWebSendEventEditMsgAction");
    const sendText = window.importNamespace("WAWebSendTextMsgChatAction");
    const sendNewsletterText = window.importNamespace("WAWebNewsletterSendMsgAction");
    const sendStatusMessage = window.importNamespace("WAWebSendStatusMsgAction");
    const mediaService = window.importNamespace("WAWebMedia");
    const mediaPrepService = mediaService;
    const mediaUpload = window.importNamespace("WAWebMediaMmsV4Upload");

    const getBlobFromMedia = get(mediaUpload, selectors.sendMessages.getBlobFromMediaObject);
    const MediaPrep = get(mediaPrepService, selectors.sendMessages.MediaPrep);
    const MediaPrepSendToChat = get(MediaPrep?.prototype, selectors.sendMessages.MediaPrepSendToChatMethod);
    const prepareMediaMethod = get(mediaService, selectors.sendMessages.prepareMediaMethod);
    const sendTextMsgToChatMethod = get(sendText, selectors.sendMessages.sendTextMsgToChatMethod);
    const sendNewsletterTextMsgToChatMethod = get(sendNewsletterText, selectors.sendMessages.sendNewsletterTextMsgToChatMethod);
    const sendStatusTextMsgMethod = get(sendStatusMessage, selectors.sendMessages.sendStatusTextMsgMethod);
    const addAndSendMsgToChatMethod = get(sendMsg, selectors.sendMessages.addAndSendMsgToChatMethod ?? "addAndSendMsgToChat");
    const sendEventEditMessageMethod = get(editEvent, selectors.sendMessages.sendEventEditMessageMethod ?? "sendEventEditMessage");

    if (
      !MediaPrep ||
      !MediaPrepSendToChat ||
      !prepareMediaMethod ||
      !sendTextMsgToChatMethod ||
      !addAndSendMsgToChatMethod ||
      !sendEventEditMessageMethod
    ) {
      this.logger.error("failed to find whatsapp api methods");
      return;
    }

    const getFileFromMediaObject = (mediaObject: any) => {
      try {
        const blob = getBlobFromMedia(mediaObject);
        const fileHash = get(mediaObject, selectors.msg.filehash);
        const mimetype = get(mediaObject, selectors.msg.mimetype);
        return new File([blob], fileHash, { type: mimetype });
      } catch (error) {
        return undefined;
      }
    };

    const originalBlob = Symbol("originalBlob");
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    Object.defineProperty(sendMsg, selectors.sendMessages.addAndSendMsgToChatMethod ?? "addAndSendMsgToChat", {
      enumerable: true,
      configurable: true,
      value: function (...args: any[]) {
        const msgObj = get(args, selectors.sendMessages.addAndSendMsgToChatMethodMsgArg ?? "1") ?? {};
        if (typeof msgObj !== "object") {
          return addAndSendMsgToChatMethod.apply(this, args);
        }
        if (msgObj.type === "interactive") {
          try {
            const orderJSON = get(msgObj, selectors.sendMessages.orderJSON ?? "interactivePayload.buttons[0].buttonParamsJson");
            const orderObj = orderJSON ? JSON.parse(orderJSON) : {};
            const items = get(orderObj, selectors.sendMessages.orderItems ?? "order.items") || [];
            const message = items
              .map((item: any) => get(item, selectors.sendMessages.orderItemName ?? "name"))
              .join("\n")
              .trim();
            self.enqueueOrderJob(message, () => {
              addAndSendMsgToChatMethod.apply(this, args);
            });
          } catch {
            self.enqueueOrderJob("", () => {
              addAndSendMsgToChatMethod.apply(this, args);
            });
          }
        } else if (msgObj.type === "event_creation") {
          self.enqueueEventJob(
            { eventName: msgObj.eventName, eventDescription: msgObj.eventDescription, eventLocation: msgObj.eventLocation?.name },
            () => {
              addAndSendMsgToChatMethod.apply(this, args);
            }
          );
        } else if (msgObj.type === "vcard") {
          self.enqueueContactJob(msgObj.body, () => {
            addAndSendMsgToChatMethod.apply(this, args);
          });
        } else if (msgObj.type === "multi_vcard") {
          const body = (Array.isArray(msgObj.vcardList) ? msgObj.vcardList : [])
            .map((card: any) => {
              return get(card, selectors.msg.vcard ?? "vcard");
            })
            .filter(Boolean)
            .join("\n")
            .trim();
          self.enqueueContactJob(body, () => {
            addAndSendMsgToChatMethod.apply(this, args);
          });
        } else if (msgObj.type === "poll_creation") {
          const options = (Array.isArray(msgObj.pollOptions) ? msgObj.pollOptions : [])
            .map((opt: any) => get(opt, selectors.msg.pollOptionName ?? "name") ?? String(opt))
            .filter(Boolean);
          self.enqueuePollJob({ pollName: msgObj.pollName, pollOptions: options }, () => {
            addAndSendMsgToChatMethod.apply(this, args);
          });
        } else if (msgObj.type === "chat") {
          self.enqueueTextJob(get(msgObj, selectors.msg.text ?? "body"), () => {
            addAndSendMsgToChatMethod.apply(this, args);
          });
        } else {
          addAndSendMsgToChatMethod.apply(this, args);
        }
      },
    });
    Object.defineProperty(editEvent, selectors.sendMessages.sendEventEditMessageMethod ?? "sendEventEditMessage", {
      enumerable: true,
      configurable: true,
      value: function (...args: any[]) {
        const msgObj = get(args, selectors.sendMessages.sendEventEditMessageMethodMsgArg ?? "0") ?? {};
        self.enqueueEventJob({ eventName: msgObj.name, eventDescription: msgObj.description, eventLocation: msgObj.location }, () => {
          sendEventEditMessageMethod.apply(this, args);
        });
      },
    });
    Object.defineProperty(mediaService, selectors.sendMessages.prepareMediaMethod, {
      enumerable: true,
      configurable: true,
      value: function (...args: any[]) {
        const res = prepareMediaMethod.apply(this, args);
        if (self.hasDLPPolicies) {
          res[originalBlob] = Promise.resolve(get(args, selectors.sendMessages.prepareMediaMethodFileArg)).then((m) => m.getBlob());
        }
        return res;
      },
    });
    Object.defineProperty(MediaPrep.prototype, selectors.sendMessages.MediaPrepSendToChatMethod, {
      enumerable: true,
      configurable: true,
      value: async function (...args: any[]) {
        const attachment = get(this, selectors.sendMessages.MediaPrepSendToMediaData);
        const blob: File = (await this[originalBlob]) || getFileFromMediaObject(attachment);
        const metadata = get(args, selectors.sendMessages.sendAttachmentMsgToChatMethodAttachmentArg);
        const mimeType = get(attachment, selectors.sendMessages.mimeType ?? "mimetype");
        const type = get(attachment, selectors.msg.type ?? "type");
        const caption = `${get(metadata, selectors.msg.textOptions[type as "chat" | "video"] ?? selectors.msg.textOptions.default) || ""}`.trim();
        self.enqueueAttachmentJob(blob, caption, mimeType, () => {
          MediaPrepSendToChat.apply(this, args);
        });
      },
    });
    Object.defineProperty(sendText, selectors.sendMessages.sendTextMsgToChatMethod, {
      enumerable: true,
      configurable: true,
      value: async function (...args: any[]) {
        const msg: string = get(args, selectors.sendMessages.sendTextMsgToChatMethodTextArg) ?? "";
        self.enqueueTextJob(msg, () => {
          sendTextMsgToChatMethod.apply(this, args);
        });
      },
    });
    Object.defineProperty(sendNewsletterText, selectors.sendMessages.sendNewsletterTextMsgToChatMethod, {
      enumerable: true,
      configurable: true,
      value: async function (...args: any[]) {
        const msg: string = get(args, selectors.sendMessages.sendNewsletterTextMsgToChatMethodTextArg) ?? "";
        self.enqueueTextJob(msg, () => {
          sendNewsletterTextMsgToChatMethod.apply(this, args);
        });
      },
    });
    Object.defineProperty(sendStatusMessage, selectors.sendMessages.sendStatusTextMsgMethod, {
      enumerable: true,
      configurable: true,
      value: async function (...args: any[]) {
        const msgText: string = get(args, selectors.sendMessages.sendStatusTextMsgMethodTextArg) ?? "";
        const shouldBlock = await self.scanTextMessage(msgText, undefined, OutboundMessageType.TEXT);
        if (shouldBlock) {
          self.sendMessage.sendMessage(msgText);
        } else {
          sendStatusTextMsgMethod.apply(this, args);
        }
      },
    });
  }

  private monkeyPatchWhatsapp() {
    if (!MessageScraper.whatsappAPI || !MessageScraper.whatsappAPI.moduleRaid || !this.selectors) {
      return;
    }
    const selectors = this.selectors;
    const sendText = MessageScraper.whatsappAPI.moduleRaid.findModule((m) => selectors.sendMessages.findTextMessage.every((path) => get(m, path)))[0];
    const mediaService = MessageScraper.whatsappAPI.moduleRaid.findModule((m) =>
      selectors.sendMessages.findPrepareMediaMessage.every((path) => get(m, path))
    )[selectors.sendMessages.findPrepareMediaIndex];
    const mediaPrepService = MessageScraper.whatsappAPI!.moduleRaid.findModule((m) =>
      selectors.sendMessages.findMediaPrep.every((path) => get(m, path))
    )[0];

    const MediaPrep = get(mediaPrepService, selectors.sendMessages.MediaPrep);
    const MediaPrepSendToChat = get(MediaPrep?.prototype, selectors.sendMessages.MediaPrepSendToChatMethod);
    const prepareMediaMethod = get(mediaService, selectors.sendMessages.prepareMediaMethod);
    const sendTextMsgToChatMethod = get(sendText, selectors.sendMessages.sendTextMsgToChatMethod);

    if (!MediaPrep || !MediaPrepSendToChat || !prepareMediaMethod || !sendTextMsgToChatMethod) {
      this.logger.error("failed to find whatsapp api methods");
      return;
    }

    const originalBlob = Symbol("originalBlob");
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    Object.defineProperty(mediaService, selectors.sendMessages.prepareMediaMethod, {
      enumerable: true,
      configurable: true,
      value: function (...args: any[]) {
        const res = prepareMediaMethod.apply(this, args);
        if (self.hasDLPPolicies) {
          res[originalBlob] = Promise.resolve(get(args, selectors.sendMessages.prepareMediaMethodFileArg)).then((m) => m.getBlob());
        }
        return res;
      },
    });
    Object.defineProperty(MediaPrep.prototype, selectors.sendMessages.MediaPrepSendToChatMethod, {
      enumerable: true,
      configurable: true,
      value: async function (...args: any[]) {
        const blob: File = await this[originalBlob];
        const metadata = get(args, selectors.sendMessages.sendAttachmentMsgToChatMethodAttachmentArg);
        const attachment = get(this, selectors.sendMessages.MediaPrepSendToMediaData);
        const mimeType = get(attachment, selectors.sendMessages.mimeType ?? "mimetype");
        const type = get(attachment, selectors.msg.type ?? "type");
        const caption = `${get(metadata, selectors.msg.textOptions[type as "chat" | "video"] ?? selectors.msg.textOptions.default) || ""}`.trim();
        self.enqueueAttachmentJob(blob, caption, mimeType, () => {
          MediaPrepSendToChat.apply(this, args);
        });
      },
    });
    Object.defineProperty(sendText, selectors.sendMessages.sendTextMsgToChatMethod, {
      enumerable: true,
      configurable: true,
      value: async function (...args: any[]) {
        const msg: string = get(args, selectors.sendMessages.sendTextMsgToChatMethodTextArg) ?? "";
        self.enqueueTextJob(msg, () => {
          sendTextMsgToChatMethod.apply(this, args);
        });
      },
    });
  }

  async onContactChanged() {
    this.hideForbiddenElements();
    this.addPasteEventListener();
  }
  override async init(selectors: WhatsappSelectorsType) {
    super.init(selectors);
    this.sendMessage.init?.(selectors);
    contentBackend.on(ContentCommands.CONTENT_READY, async () => {
      MessageScraper.inject(selectors);
      this.observeElements();
      window.importNamespace === undefined ? this.monkeyPatchWhatsapp() : this.monkeyPatchWhatsappComet();
      this.addPasteEventListener();
    });
  }

  protected handleShowOnlyChannelsClass(): void {
    this.lockToChannelsTab();
  }

  #cleanupChannelsLock() {
    if (this.#chatListVisibilityChangeHandler) {
      try {
        const WAWebCmd = window.importNamespace("WAWebCmd");
        if (WAWebCmd?.Cmd?.off) {
          WAWebCmd.Cmd.off("chat_list_visibility_change", this.#chatListVisibilityChangeHandler);
        }
      } catch (error) {
        this.logger.error({ error }, "Failed to remove chat list visibility listener");
      }
      this.#chatListVisibilityChangeHandler = undefined;
    }
  }

  private clickChannelsTab(): void {
    if (!this.selectors?.navigation?.tabs?.channels) {
      return;
    }

    const channelsTab = document.querySelector(this.selectors.navigation.tabs.channels);
    if (channelsTab && channelsTab instanceof HTMLElement) {
      channelsTab.click();
    }
  }

  private lockToChannelsTab() {
    if (this.#isLockedToChannelsTab) {
      return;
    }

    this.#isLockedToChannelsTab = true;
    this.clickChannelsTab();
    try {
      const WAWebCmd = window.importNamespace("WAWebCmd");
      if (WAWebCmd?.Cmd?.on) {
        this.#chatListVisibilityChangeHandler = () => {
          if (this.#isLockedToChannelsTab) {
            // Delay click to ensure DOM updates are complete after visibility change
            setTimeout(() => this.clickChannelsTab(), 1);
          }
        };

        WAWebCmd.Cmd.on("chat_list_visibility_change", this.#chatListVisibilityChangeHandler);
      }
    } catch (err) {
      this.logger.error({ err }, "Failed to set up channels lock event listener");
    }
  }
}
